import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import SEO from "@/components/seo/SEO";
import { Eyebrow, AmbientOrbs } from "@/components/common/ui";
import { submitDemo } from "@/lib/api";

export default function BookDemo() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const types = t("demo.property_types", { returnObjects: true }) || [];
  const rooms = t("demo.room_counts", { returnObjects: true }) || [];

  const [form, setForm] = useState({
    name: "", email: "", company: "", role: "",
    property_type: types[0] || "", room_count: rooms[0] || "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await submitDemo({ ...form, lang });
      setSent(true);
      toast.success(t("demo.success"));
      setForm({ name: "", email: "", company: "", role: "", property_type: types[0] || "", room_count: rooms[0] || "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <>
      <SEO title={lang === "nl" ? "Demo aanvragen — BHT One" : "Book a demo — BHT One"} path="/book-demo" />
      <section className="relative overflow-hidden py-32 lg:py-44">
        <AmbientOrbs />
        <div className="mx-auto grid max-w-[1400px] gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-5">
            <Eyebrow>{t("demo.eyebrow")}</Eyebrow>
            <h1 className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl">{t("demo.h1")}</h1>
            <p className="mt-8 max-w-md text-lg text-white/65">{t("demo.sub")}</p>
            <ul className="mt-12 space-y-3 text-sm text-white/60">
              <li className="flex items-start gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-bht-gold" /> {lang === "nl" ? "30 minuten, geen slides." : "30 minutes, no slides."}</li>
              <li className="flex items-start gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-bht-gold" /> {lang === "nl" ? "Live BHT One in uw context." : "Live BHT One in your context."}</li>
              <li className="flex items-start gap-3"><span className="mt-2 h-1 w-3 shrink-0 bg-bht-gold" /> {lang === "nl" ? "Antwoord binnen één werkdag." : "Reply within one business day."}</li>
            </ul>
          </div>

          <form onSubmit={onSubmit} className="glass rounded-2xl p-8 lg:col-span-7" data-testid="demo-form">
            <div className="grid gap-5 md:grid-cols-2">
              <Field id="name" label={t("demo.name")} value={form.name} onChange={set("name")} required testId="demo-name" />
              <Field id="email" type="email" label={t("demo.email")} value={form.email} onChange={set("email")} required testId="demo-email" />
              <Field id="company" label={t("demo.company")} value={form.company} onChange={set("company")} required testId="demo-company" />
              <Field id="role" label={t("demo.role")} value={form.role} onChange={set("role")} testId="demo-role" />
              <Select id="property_type" label={t("demo.property_type")} value={form.property_type} onChange={set("property_type")} options={types} testId="demo-property-type" />
              <Select id="room_count" label={t("demo.room_count")} value={form.room_count} onChange={set("room_count")} options={rooms} testId="demo-room-count" />
              <Field id="message" as="textarea" label={t("demo.message")} value={form.message} onChange={set("message")} className="md:col-span-2" rows={4} testId="demo-message" />
            </div>
            <button
              type="submit"
              disabled={busy || sent}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-bht-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-bht-void transition-transform hover:scale-[1.03] disabled:opacity-50"
              data-testid="demo-submit"
            >
              {busy ? "…" : sent ? "✓" : t("demo.submit")}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ id, label, value, onChange, type = "text", as, required, className = "", rows, testId }) {
  const base = "w-full rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-white placeholder:text-white/30 focus:border-bht-gold/60 focus:outline-none";
  return (
    <label className={`block ${className}`}>
      <span className="eyebrow mb-2 block">{label}{required && " *"}</span>
      {as === "textarea" ? (
        <textarea id={id} required={required} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={base} data-testid={testId} />
      ) : (
        <input id={id} required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)} className={base} data-testid={testId} />
      )}
    </label>
  );
}

function Select({ id, label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="eyebrow mb-2 block">{label}</span>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-white focus:border-bht-gold/60 focus:outline-none"
        data-testid={testId}
      >
        {options.map((o) => <option key={o} value={o} className="bg-bht-navy2">{o}</option>)}
      </select>
    </label>
  );
}
